/**
 * Rhythmz — VisualizerEngine (WebGPU generative core)
 * ──────────────────────────────────────────────────
 * Owns the WebGPURenderer, the fullscreen quad, shared uniforms, audio analysis,
 * preset management, and the render loop. The fragment shader is built from
 * shader.ts (module-level TSL — reliable composition).
 *
 * SIZING IS LOAD-BEARING: canvas drawing buffer = display size × min(dpr,2).
 */

import * as THREE from 'three/webgpu';
import { RenderPipeline } from 'three/webgpu';
import { uniform, pass } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { fxaa } from 'three/addons/tsl/display/FXAANode.js';
import type { AudioSourceManager } from '@audio/AudioSourceManager';
import { AudioAnalyzer, type AudioFeatures } from './AudioAnalyzer';
import { buildShader } from './shader';
import { VideoLayer } from './VideoLayer';
import { SceneManager } from '../scenes/SceneManager';
import { createCrystallineSpire } from '../scenes/CrystallineSpire';
import { createKaleidoscopicTorus } from '../scenes/KaleidoscopicTorus';
import { createParticleNebula } from '../scenes/ParticleNebula';
import { createFractalTunnel } from '../scenes/FractalTunnel';
import { createGeometricBloom } from '../scenes/GeometricBloom';
import { createLightBeams } from '../scenes/LightBeams';
import { createPlasmaGrid } from '../scenes/PlasmaGrid';
import { createTheVoidV2 } from '../scenes/TheVoidV2';
import { createBasiliskSkull } from '../scenes/BasiliskSkull';
import { createCrystalColossus } from '../scenes/CrystalColossus';
import { createMechanicalBeast } from '../scenes/MechanicalBeast';
import { createFractalDragon } from '../scenes/FractalDragon';
import { createBioluminescentForest } from '../scenes/BioluminescentForest';
import { createCuttlefish } from '../scenes/Cuttlefish';
import { createGeometricMandala } from '../scenes/GeometricMandala';

export interface EngineStats { fps: number; presetName: string; }

export interface EnginePreset {
  name: string;
  generator?: string;
  palette?: { r: number; g: number; b: number }[];
  params?: Partial<{
    speed: number; warp: number; complexity: number; scale: number;
    rotation: number; hueShift: number; intensity: number;
  }>;
  bindings?: unknown[];
  data?: unknown;
}

export class VisualizerEngine {
  private renderer: THREE.WebGPURenderer;
  private postProcessing: RenderPipeline | null = null;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private material: THREE.NodeMaterial;
  private mesh: THREE.Mesh;
  private bgGroup: THREE.Group;
  private bgGeo: THREE.PlaneGeometry;
  private objects3D: THREE.Object3D[] = [];

  private audio: AudioSourceManager;
  private analyzer: AudioAnalyzer;
  private canvas: HTMLCanvasElement;

  private presets: EnginePreset[] = [];
  private currentIdx = 0;
  private currentName = 'Rhythmz';
  private autoRotate = true;
  private rotateMs = 30000;
  private lastRotate = 0;

  private running = false;
  private initialized = false;
  private resizeObserver: ResizeObserver | null = null;
  private frames = 0;
  private lastFpsT = performance.now();
  private onStats?: (s: EngineStats) => void;

  // Shared uniforms
  private uTime = uniform(0);
  private uBass = uniform(0);
  private uMid = uniform(0);
  private uTreble = uniform(0);
  private uLevel = uniform(0);
  private uBeat = uniform(0);
  private uSpeed = uniform(1);
  private uWarp = uniform(0.5);
  private uScale = uniform(1);
  private uComplexity = uniform(0.6);
  private uIntensity = uniform(1);
  private uRotation = uniform(0.2);
  private uHueShift = uniform(0);
  private uCol0 = uniform(new THREE.Color(0.9, 0.15, 0.55));
  private uCol1 = uniform(new THREE.Color(0.05, 0.45, 0.75));
  private uCol2 = uniform(new THREE.Color(0.95, 0.5, 0.1));
  private uAspect = uniform(16 / 9);
  private uBloomStrength = uniform(0.2);

  private currentGenerator = 'warp';
  private videoLayer: VideoLayer | null = null;
  private sceneManager: SceneManager | null = null;
  private audioReactive = true;
  /** Public accessor for SceneDirector integration. */
  public get sceneManagerAccess(): SceneManager | null { return this.sceneManager; }
  private lastFrameTime = performance.now();

  constructor(canvas: HTMLCanvasElement, audio: AudioSourceManager) {
    this.canvas = canvas;
    this.audio = audio;

    // 2026 best practice: WebGPU renderer with antialias + tone mapping for HDR fidelity
    this.renderer = new THREE.WebGPURenderer({ canvas, antialias: true });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.analyzer = new AudioAnalyzer(audio.output, audio.ctx);

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(60, 16 / 9, 0.1, 100);
    this.camera.position.set(0, 0, 4);

    this.material = this.makeMaterial();
    this.bgGroup = new THREE.Group();
    this.bgGroup.position.z = -10;
    this.bgGeo = new THREE.PlaneGeometry(2, 2);
    this.mesh = new THREE.Mesh(this.bgGeo, this.material);
    this.bgGroup.add(this.mesh);
    this.bgGroup.renderOrder = -1;
    this.scene.add(this.bgGroup);

    window.addEventListener('resize', this.onResize);
    this.applyPreset(this.defaultPreset());
  }

  private defaultPreset(): EnginePreset {
    return {
      name: 'Excision',
      params: { speed: 0.7, warp: 0.4, scale: 1.6, intensity: 0.4 },
      palette: [
        { r: 0.9, g: 0.15, b: 0.55 },
        { r: 0.05, g: 0.45, b: 0.75 },
        { r: 0.95, g: 0.5, b: 0.1 },
      ],
    };
  }

  private uniforms() {
    return {
      uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uTreble: this.uTreble,
      uLevel: this.uLevel, uBeat: this.uBeat, uSpeed: this.uSpeed, uWarp: this.uWarp,
      uScale: this.uScale, uComplexity: this.uComplexity, uIntensity: this.uIntensity,
      uRotation: this.uRotation, uHueShift: this.uHueShift,
      uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2, uAspect: this.uAspect,
    };
  }

  private makeMaterial(): THREE.NodeMaterial {
    const m = new THREE.NodeMaterial();
    m.fragmentNode = buildShader(this.currentGenerator, this.uniforms());
    m.depthTest = false;
    m.depthWrite = false;
    return m;
  }

  private rebuildMaterial(): void {
    this.material.fragmentNode = buildShader(this.currentGenerator, this.uniforms());
    this.material.needsUpdate = true;
    this.mesh.material = this.material;
  }

  /* ─────────────── Preset API ─────────────── */

  loadPresets(presets: EnginePreset[]): void {
    this.presets = presets;
    if (presets.length > 0) this.loadPreset(0);
  }
  loadPreset(idx: number): void {
    if (idx < 0 || idx >= this.presets.length) return;
    this.currentIdx = idx;
    this.currentName = this.presets[idx].name;
    this.applyPreset(this.presets[idx]);
    this.emitStats();
  }
  addPreset(name: string, data: unknown): number {
    if (!data || typeof data !== 'object') return this.currentIdx;
    this.presets.push({ ...(data as EnginePreset), name });
    const idx = this.presets.length - 1;
    this.loadPreset(idx);
    return idx;
  }
  loadRandom(): void {
    if (this.presets.length === 0) return;
    let n = this.currentIdx;
    while (n === this.currentIdx && this.presets.length > 1) n = Math.floor(Math.random() * this.presets.length);
    this.loadPreset(n);
  }
  nextPreset(): void { if (this.presets.length) this.loadPreset((this.currentIdx + 1) % this.presets.length); }
  prevPreset(): void { if (this.presets.length) this.loadPreset((this.currentIdx - 1 + this.presets.length) % this.presets.length); }
  setAutoRotate(on: boolean, intervalMs?: number): void {
    this.autoRotate = on;
    if (intervalMs) this.rotateMs = intervalMs;
    this.lastRotate = performance.now();
  }
  setStatsListener(fn: (s: EngineStats) => void): void { this.onStats = fn; }
  setSpeed(v: number): void { this.uSpeed.value = Math.max(0, Math.min(2, v)); }
  setWarp(v: number): void { this.uWarp.value = Math.max(0, Math.min(1, v)); }
  setIntensity(v: number): void { this.uIntensity.value = Math.max(0, Math.min(2, v)); }
  setScale(v: number): void { this.uScale.value = Math.max(0.1, Math.min(3, v)); }
  setComplexity(v: number): void { this.uComplexity.value = Math.max(0, Math.min(1, v)); }
  setRotation(v: number): void { this.uRotation.value = Math.max(-1, Math.min(1, v)); }
  setHueShift(v: number): void { this.uHueShift.value = Math.max(0, Math.min(1, v)); }
  setBloomStrength(v: number): void { this.uBloomStrength.value = Math.max(0, Math.min(0.5, v)); }
  /** Set a palette color by index (0, 1, or 2) from RGB 0-1 values. */
  setPaletteColor(idx: number, r: number, g: number, b: number): void {
    const clamped = (v: number) => Math.max(0, Math.min(1, v));
    const c = new THREE.Color(clamped(r), clamped(g), clamped(b));
    if (idx === 0) this.uCol0.value.copy(c);
    else if (idx === 1) this.uCol1.value.copy(c);
    else if (idx === 2) this.uCol2.value.copy(c);
  }
  /** When false, the render loop stops feeding live audio into uniforms (for AI-only control). */
  setAudioReactive(on: boolean): void { this.audioReactive = on; }
  get isAudioReactive(): boolean { return this.audioReactive; }
  get currentPresetName(): string { return this.currentName; }
  get currentPresetIdx(): number { return this.currentIdx; }
  get presetCount(): number { return this.presets.length; }

  /** Get the latest audio features for external AI consumption. */
  getAudioFeatures(): AudioFeatures {
    const now = performance.now();
    const playing = this.audio.element ? !this.audio.element.paused : false;
    return playing ? this.analyzer.sample(now) : this.analyzer.idle();
  }

  /** Serialize the current engine state for saving/demoing. */
  serializeState(): Record<string, unknown> {
    return {
      name: this.currentName,
      generator: this.currentGenerator,
      palette: [
        { r: this.uCol0.value.r, g: this.uCol0.value.g, b: this.uCol0.value.b },
        { r: this.uCol1.value.r, g: this.uCol1.value.g, b: this.uCol1.value.b },
        { r: this.uCol2.value.r, g: this.uCol2.value.g, b: this.uCol2.value.b },
      ],
      params: {
        speed: this.uSpeed.value,
        warp: this.uWarp.value,
        scale: this.uScale.value,
        complexity: this.uComplexity.value,
        intensity: this.uIntensity.value,
        rotation: this.uRotation.value,
        hueShift: this.uHueShift.value,
      },
      sceneIdx: this.sceneManager?.currentSceneName ?? null,
      timestamp: Date.now(),
    };
  }

  private applyPreset(p: EnginePreset): void {
    const pm = p.params ?? {};
    if (pm.speed != null) this.uSpeed.value = pm.speed;
    if (pm.warp != null) this.uWarp.value = pm.warp;
    if (pm.scale != null) this.uScale.value = pm.scale;
    if (pm.complexity != null) this.uComplexity.value = pm.complexity;
    if (pm.intensity != null) this.uIntensity.value = pm.intensity;
    if (pm.rotation != null) this.uRotation.value = pm.rotation;
    if (pm.hueShift != null) this.uHueShift.value = pm.hueShift;
    const pal = p.palette;
    if (pal && pal.length >= 2) {
      this.uCol0.value.setRGB(pal[0].r, pal[0].g, pal[0].b);
      this.uCol1.value.setRGB(pal[1].r, pal[1].g, pal[1].b);
      this.uCol2.value.setRGB((pal[2] ?? pal[1]).r, (pal[2] ?? pal[1]).g, (pal[2] ?? pal[1]).b);
      this.videoLayer?.setPalette(this.uCol0.value, this.uCol1.value);
    }
    if (p.generator && p.generator !== this.currentGenerator) {
      this.currentGenerator = p.generator;
      this.rebuildMaterial();
    }
  }

  /* ─────────────── Video layer ─────────────── */

  async loadVideo(file: File): Promise<void> {
    this.removeVideo();
    this.videoLayer = await VideoLayer.fromFile(file);
    this.videoLayer.setPalette(this.uCol0.value, this.uCol1.value);
    this.scene.add(this.videoLayer.mesh);
  }
  removeVideo(): void {
    if (this.videoLayer) {
      this.scene.remove(this.videoLayer.mesh);
      this.videoLayer.dispose();
      this.videoLayer = null;
    }
  }
  get hasVideo(): boolean { return this.videoLayer !== null; }
  setVideoGradeMix(v: number): void { this.videoLayer?.setGradeMix(v); }
  setVideoLumaKey(v: number): void { this.videoLayer?.setLumaKey(v); }
  setVideoBehind(behind: boolean): void { this.videoLayer?.setShowBehind(behind); }

  /* ─────────────── Render loop ─────────────── */

  async start(): Promise<void> {
    if (this.running) return;
    if (!this.initialized) { await this.renderer.init(); this.initialized = true; }
    this.sizeCanvas();
    this.setupPostProcessing();

    // Scene manager with all 8 scenes
    this.sceneManager = new SceneManager({ autoCycleInterval: 15000, transitionSeconds: 1.5 });
    this.sceneManager.attach(this.scene);
    this.sceneManager.register(
      () => createCrystallineSpire({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createKaleidoscopicTorus({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createParticleNebula({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createFractalTunnel({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createGeometricBloom({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createLightBeams({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createPlasmaGrid({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createTheVoidV2({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createBasiliskSkull({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createCrystalColossus({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createMechanicalBeast({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createFractalDragon({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createBioluminescentForest({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createCuttlefish({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
      () => createGeometricMandala({ uTime: this.uTime, uBass: this.uBass, uMid: this.uMid, uBeat: this.uBeat, uTreble: this.uTreble, uCol0: this.uCol0, uCol1: this.uCol1, uCol2: this.uCol2 }),
    );
    this.sceneManager.start();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.sizeCanvas());
      this.resizeObserver.observe(this.canvas);
    }
    this.running = true;
    this.lastFpsT = performance.now();
    this.lastRotate = performance.now();
    this.renderer.setAnimationLoop(this.loop);
  }

  /**
   * Build the post-processing chain (2026 best practices):
   * 1. Scene pass → color texture
   * 2. Selective bloom (only highlights, threshold 0.75)
   * 3. FXAA for edge sharpness (complements hardware MSAA)
   * 4. ACES Filmic tone mapping (set on renderer) for HDR fidelity
   */
  private setupPostProcessing(): void {
    const scenePass = pass(this.scene, this.camera);
    const sceneColor = scenePass.getTextureNode('output');

    // Selective bloom — only the brightest highlights glow
    const bloomed = sceneColor.add(bloom(sceneColor, this.uBloomStrength, 0.75, 0.4));

    // FXAA for crisp edges — 2026 recommendation for post-process AA
    const sharpened = fxaa(bloomed);

    this.postProcessing = new RenderPipeline(this.renderer, sharpened);
  }

  stop(): void { this.running = false; this.renderer.setAnimationLoop(null); }

  private loop = (): void => {
    const now = performance.now();
    this.uTime.value = now / 1000;
    // Only feed live audio into uniforms when audio-reactive mode is on
    if (this.audioReactive) {
      const playing = this.audio.element ? !this.audio.element.paused : false;
      const af: AudioFeatures = playing ? this.analyzer.sample(now) : this.analyzer.idle();
      this.uBass.value = af.bass; this.uMid.value = af.mid; this.uTreble.value = af.treble;
      this.uLevel.value = af.level; this.uBeat.value = af.beat;
      this.uBloomStrength.value = 0.08 + af.beat * 0.12;
      this.videoLayer?.updateAudio(af);
    }
    const dt = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;
    this.sceneManager?.update(dt);
    // Update SceneArchitect effects with current audio
    if (this.effects.length > 0) {
      const audioRef = {
        bass: this.uBass.value as number,
        mid: this.uMid.value as number,
        treble: this.uTreble.value as number,
        level: this.uLevel.value as number,
        beat: this.uBeat.value as number,
      };
      for (const e of this.effects) e.update(dt, audioRef);
    }
    if (this.autoRotate && now - this.lastRotate > this.rotateMs) { this.loadRandom(); this.lastRotate = now; }
    if (this.postProcessing) this.postProcessing.render();
    else this.renderer.render(this.scene, this.camera);
    this.frames++;
    if (now - this.lastFpsT >= 1000) { this.emitStats(this.frames); this.frames = 0; this.lastFpsT = now; }
  };

  private emitStats(fps?: number): void { this.onStats?.({ fps: fps ?? 0, presetName: this.currentName }); }

  /* ─────────────── Sizing ─────────────── */

  private sizeCanvas(): void {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.max(2, Math.floor(w * dpr));
    this.canvas.height = Math.max(2, Math.floor(h * dpr));
    const aspect = w / h;
    this.uAspect.value = aspect;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    const dist = Math.abs(this.camera.position.z - this.bgGroup.position.z);
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const viewH = 2 * Math.tan(fovRad / 2) * dist;
    const viewW = viewH * aspect;
    this.bgGroup.scale.set(viewW / 2, viewH / 2, 1);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
  }
  private onResize = (): void => { this.sizeCanvas(); };

  add3DObject(obj: THREE.Object3D): void {
    this.scene.add(obj);
    this.objects3D.push(obj);
  }
  remove3DObject(obj: THREE.Object3D): void {
    this.scene.remove(obj);
    this.objects3D = this.objects3D.filter((o) => o !== obj);
  }
  clear3DObjects(): void {
    for (const o of this.objects3D) this.scene.remove(o);
    this.objects3D = [];
  }

  /* ─────────────── Scene Effects (SceneArchitect) ─────────────── */

  private effects: Array<{ group: THREE.Object3D; update: (dt: number, audio: { bass: number; mid: number; treble: number; level: number; beat: number }) => void; dispose: () => void }> = [];

  /** Add a SceneArchitect-built effect to the scene with audio-reactive updates. */
  addEffect(effect: { group: THREE.Object3D; update: (dt: number, audio: { bass: number; mid: number; treble: number; level: number; beat: number }) => void; dispose: () => void }): void {
    this.scene.add(effect.group);
    this.effects.push(effect);
  }

  /** Remove all SceneArchitect effects. */
  clearEffects(): void {
    for (const e of this.effects) {
      this.scene.remove(e.group);
      e.dispose();
    }
    this.effects = [];
  }

  dispose(): void {
    this.stop();
    window.removeEventListener('resize', this.onResize);
    this.resizeObserver?.disconnect();
    this.analyzer.dispose();
    this.bgGeo.dispose();
    this.material.dispose();
    this.sceneManager?.dispose();
    this.renderer.dispose();
  }
}
